import { memo, ReactNode, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { ThemeModeContext, ThemeModeContextType, ThemeModeType } from "./ThemeModeContext";

export type ThemeModeProviderProps = {
  children?: ReactNode | ReactNode[],
  themeStorageKey?: string,
  darkModeClassName?: string
  lightModeClassName?: string,
  defaultThemeMode?: ThemeModeType
  useAutoTheme?: boolean
}

export const ThemeModeProvider = memo<ThemeModeProviderProps>((
  {
    children,
    themeStorageKey = "mimic-theme",
    darkModeClassName = "mimic-dark-mode",
    lightModeClassName = "mimic-light-mode",
    defaultThemeMode = "light",
    useAutoTheme = true,
  }) => {
  const [ userPreferredTheme, setUserPreferredTheme ] = useState<ThemeModeType | null>(null);
  const [ prefersDark, setPrefersDark ] = useState(false);
  const [ theme, setTheme ] = useState<ThemeModeType>(defaultThemeMode);
  const [ themeBackgroundColor, setThemeBackgroundColor ] = useState("");

  useEffect(() => {
    setUserPreferredTheme((JSON.parse(window.localStorage.getItem(themeStorageKey) || "null") ?? null) as ThemeModeType | null);
  }, [ themeStorageKey ]);

  useEffect(() => {
    const prefersDarkScheme = window.matchMedia("(prefers-color-scheme: dark)");

    setPrefersDark(prefersDarkScheme.matches);

    const listener = (ev: MediaQueryListEvent) => {
      setPrefersDark(ev.matches);
    };

    prefersDarkScheme.addEventListener("change", listener);
    return () => {
      prefersDarkScheme.removeEventListener("change", listener);
    };
  }, []);

  useEffect(() => {
    if (userPreferredTheme) {
      setTheme(userPreferredTheme);
    } else {
      if (useAutoTheme) {
        setTheme(prefersDark ? "dark" : "light");
      } else {
        setTheme(defaultThemeMode);
      }
    }
  }, [ userPreferredTheme, prefersDark, useAutoTheme, defaultThemeMode ]);

  useEffect(() => {
    window.localStorage.setItem(themeStorageKey, JSON.stringify(userPreferredTheme));
  }, [ userPreferredTheme, themeStorageKey ]);

  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.remove(lightModeClassName);
      document.documentElement.classList.add(darkModeClassName);
    } else {
      document.documentElement.classList.remove(darkModeClassName);
      document.documentElement.classList.add(lightModeClassName);
    }
  }, [ theme, lightModeClassName, darkModeClassName ]);

  const themeContextValue = useMemo(() => {
    const systemPreferredTheme = prefersDark ? "dark" : "light";
    return ({
      theme,
      systemPreferredTheme,
      userPreferredTheme,
      setUserPreferredTheme,
    } as ThemeModeContextType);
  }, [ theme, prefersDark, userPreferredTheme ]);

  useLayoutEffect(() => {
    setTimeout(() => {
      setThemeBackgroundColor(getComputedStyle(document.body)
        .getPropertyValue("--bg-primary"));
    }, 8);
  }, [ theme ]);

  return (
    <ThemeModeContext.Provider value={themeContextValue}>
      <Helmet key="helmet">
        <meta name="theme-color" content={themeBackgroundColor} />
      </Helmet>
      {children}
    </ThemeModeContext.Provider>
  );
});